import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Package, Upload, Plus, Trash, MagnifyingGlass, Download, X, FileCsv } from "@phosphor-icons/react";
import CsvImportResultDialog from "@/components/CsvImportResultDialog";
import ConfirmDialog from "@/components/ConfirmDialog";

const SAMPLE_CSV = `ref;name;description;photo_url;price;category
LEG-067631;Prise 2P+T Céliane blanc;Prise saillie blanche 16A;https://cdn.legrand.fr/celiane-prise.jpg;12.90;Prise
SCH-A9F04616;Disjoncteur Acti9 iC60N 16A courbe C;Disj 1P+N monobloc 16A 6kA;https://cdn.se.com/ic60n.jpg;28.50;Disjoncteur
HAG-CDA716F;Diff CDA 30mA type A 40A;Différentiel type A 4 modules;https://cdn.hager.fr/cda.jpg;68.00;Différentiel
DAI-FXFA25A;Cassette 4 voies FXFA25A;Cass. 2.5kW VRV IV;https://cdn.daikin.com/fxfa.jpg;;Climatisation`;

export default function MaterialCatalogPanel() {
  const { has } = useAuth();
  const canSeePrice = has ? has("chantiers.view.prices") : false;
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [importFilename, setImportFilename] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef(null);

  const load = async (search = q) => {
    setLoading(true);
    try {
      const r = await api.get("/material-catalog", { params: search ? { q: search } : {} });
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => load(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const uploadCsv = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name) && file.type !== "text/csv") {
      toast.error("Merci de fournir un fichier .csv");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/material-catalog/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportFilename(file.name);
      setImportResult(r.data);
      load("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) uploadCsv(file);
  };

  const addManual = async () => {
    if (!form.ref?.trim() || !form.name?.trim()) return toast.error("Référence et nom requis");
    try {
      await api.post("/material-catalog", { ...form, unit_price: form.unit_price ? parseFloat(form.unit_price) : null });
      setForm({});
      setAddOpen(false);
      load("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/material-catalog/${toDelete.id}`);
      setItems((s) => s.filter((x) => x.id !== toDelete.id));
      setToDelete(null);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bkflow-catalogue-modele.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3" data-testid="material-catalog-panel">
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#005CFF]/20 flex items-center justify-center shrink-0">
            <Package size={20} weight="duotone" className="text-[#005CFF]" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold">Catalogue matériel</div>
            <div className="text-[11px] text-white/50">Photos + références pour reconnaissance visuelle sur chantier · {items.length} articles</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher : réf, nom, description…"
              className="bg-white/5 border-white/10 pl-9"
              data-testid="cat-search"
            />
          </div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => uploadCsv(e.target.files?.[0])} className="hidden" data-testid="cat-csv-input" />
          <Button variant="ghost" onClick={downloadSample} className="border border-white/10 h-10" data-testid="cat-sample-btn" title="Télécharger un modèle de CSV">
            <Download size={14} weight="bold" />
            <span className="hidden sm:inline text-xs ml-1">Modèle</span>
          </Button>
          <Button onClick={() => setAddOpen((v) => !v)} className="bg-[#005CFF] hover:bg-[#0047CC] h-10" data-testid="cat-add-btn">
            <Plus size={14} weight="bold" /> Ajouter
          </Button>
        </div>

        {/* Drag & Drop / Click CSV import zone */}
        <div
          onClick={() => !busy && fileRef.current?.click()}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`rounded-xl border-2 border-dashed p-4 mb-3 cursor-pointer transition-colors flex items-center gap-3 ${
            dragOver
              ? "border-[#005CFF] bg-[#005CFF]/10"
              : busy
                ? "border-white/10 bg-white/[0.02] opacity-70 cursor-progress"
                : "border-white/15 bg-white/[0.02] hover:border-[#005CFF]/50 hover:bg-[#005CFF]/5"
          }`}
          data-testid="cat-csv-dropzone"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}
        >
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${busy ? "bg-white/5 animate-pulse" : "bg-[#005CFF]/15"}`}>
            {busy ? (
              <div className="w-5 h-5 rounded-full border-2 border-white/20 border-t-[#005CFF] animate-spin" />
            ) : (
              <FileCsv size={20} weight="duotone" className="text-[#005CFF]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm">
              {busy ? "Import en cours…" : dragOver ? "Lâche le fichier pour l'importer" : "Importer un catalogue CSV"}
            </div>
            <div className="text-[11px] text-white/50 truncate">
              {busy ? "Merci de patienter" : "Glisse-dépose ton fichier ici ou clique pour parcourir · en-têtes: ref, name, description, photo_url, price, category"}
            </div>
          </div>
          {!busy && (
            <div className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#005CFF]/20 border border-[#005CFF]/40 text-[10px] uppercase tracking-widest text-[#7CB2FF] shrink-0">
              <Upload size={11} weight="bold" /> CSV
            </div>
          )}
        </div>

        {addOpen && (
          <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 mb-3 grid grid-cols-2 gap-2" data-testid="cat-add-form">
            <Input value={form.ref || ""} onChange={(e) => setForm({ ...form, ref: e.target.value })} placeholder="Référence *" className="bg-white/5 border-white/10 col-span-1" data-testid="cat-form-ref" />
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom *" className="bg-white/5 border-white/10 col-span-1" data-testid="cat-form-name" />
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="bg-white/5 border-white/10 col-span-2" />
            <Input value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="URL photo" className="bg-white/5 border-white/10 col-span-2" />
            <Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Catégorie" className="bg-white/5 border-white/10 col-span-1" />
            <Input value={form.unit_price || ""} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="Prix (admin)" type="number" step="0.01" className="bg-white/5 border-white/10 col-span-1" />
            <div className="col-span-2 flex gap-2">
              <Button variant="ghost" onClick={() => { setAddOpen(false); setForm({}); }} className="border border-white/10 flex-1">Annuler</Button>
              <Button onClick={addManual} className="bg-[#005CFF] hover:bg-[#0047CC] flex-1" data-testid="cat-form-save">Créer</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-white/40 text-sm text-center py-6">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-white/[0.02] border border-dashed border-white/10 p-8 text-center text-white/40 text-sm">
            Catalogue vide. Importe un CSV ou ajoute un article.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden group" data-testid={`cat-item-${it.id}`}>
                <div className="aspect-square bg-black/40 relative">
                  {it.photo_url ? (
                    <img src={it.photo_url} alt={it.name} className="w-full h-full object-contain" loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/20"><Package size={38} weight="duotone" /></div>
                  )}
                  <button onClick={() => setToDelete(it)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 backdrop-blur-sm opacity-0 group-hover:opacity-100 flex items-center justify-center text-red-400 hover:text-red-300" title="Supprimer" data-testid={`cat-del-${it.id}`}>
                    <Trash size={11} />
                  </button>
                </div>
                <div className="p-2">
                  <div className="text-[10px] font-mono text-white/50 truncate">{it.ref}</div>
                  <div className="text-xs font-medium truncate">{it.name}</div>
                  {it.category && <div className="text-[9px] uppercase tracking-widest text-white/40 mt-0.5">{it.category}</div>}
                  {canSeePrice && it.unit_price != null && (
                    <div className="text-[10px] font-mono tabular-nums text-emerald-400 mt-1">{it.unit_price.toFixed(2)} €</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CsvImportResultDialog
        open={!!importResult}
        onOpenChange={(o) => !o && setImportResult(null)}
        result={importResult}
        filename={importFilename}
      />

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(null)}
        onConfirm={doDelete}
        title="Supprimer cet article ?"
        description={toDelete ? `« ${toDelete.name} » (${toDelete.ref}) sera retiré du catalogue matériel. Cette action est irréversible.` : ""}
        confirmLabel="Supprimer"
        testid="cat-delete-confirm"
      />
    </div>
  );
}
